import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Eye, EyeOff, CheckCircle, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resetPassword } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      navigate("/connexion");
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    if (password.length < 12) {
      setError("Le mot de passe doit contenir au moins 12 caractères.");
      return;
    }
    const classes =
      (/[a-z]/.test(password) ? 1 : 0) +
      (/[A-Z]/.test(password) ? 1 : 0) +
      (/\d/.test(password) ? 1 : 0) +
      (/[^A-Za-z0-9]/.test(password) ? 1 : 0);
    if (classes < 3) {
      setError("Le mot de passe doit combiner au moins 3 types de caractères parmi : minuscules, majuscules, chiffres, symboles.");
      return;
    }

    setLoading(true);

    const result = await resetPassword(token, password);
    
    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  if (!token) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 font-['DM_Sans'] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-lg w-full max-w-md" data-testid="reset-password-card">
          {!success ? (
            <>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-[#19294C] font-['Outfit']">
                  Nouveau mot de passe
                </h1>
                <p className="mt-2 text-slate-600">
                  Choisissez un nouveau mot de passe sécurisé.
                </p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2" data-testid="reset-error">
                  <XCircle className="w-5 h-5 flex-shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="password" className="text-slate-700">Nouveau mot de passe</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10 border-slate-300 focus:ring-[#00B8E4] focus:border-[#00B8E4]"
                      required
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">Minimum 12 caractères, avec au moins 3 types parmi : minuscules, majuscules, chiffres, symboles.</p>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-slate-700">Confirmer le mot de passe</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 border-slate-300 focus:ring-[#00B8E4] focus:border-[#00B8E4]"
                      required
                      data-testid="reset-confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#00B8E4] hover:bg-[#00a5ce] text-white py-3 font-semibold mt-2"
                  data-testid="reset-submit-btn"
                >
                  {loading ? "Réinitialisation..." : "Réinitialiser le mot de passe"}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="reset-success">
              <div className="w-16 h-16 bg-[#00B8E4]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#00B8E4]" />
              </div>
              <h1 className="text-2xl font-bold text-[#19294C] font-['Outfit']">
                Mot de passe mis à jour !
              </h1>
              <p className="mt-4 text-slate-600">
                Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.
              </p>
              <Link to="/connexion">
                <Button className="mt-6 bg-[#00B8E4] hover:bg-[#00a5ce] text-white" data-testid="reset-login-btn">
                  Se connecter
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ResetPasswordPage;
